import { Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { IKeybindingService } from 'vscode/vscode/vs/platform/keybinding/common/keybinding.service';
import { IAccessibleViewService } from 'vscode/vscode/vs/workbench/contrib/accessibility/browser/accessibleView.service';
import { AccessibilityHelpAction } from 'vscode/vscode/vs/workbench/contrib/accessibility/browser/accessibleViewActions';
import { ITerminalService } from 'vscode/vscode/vs/workbench/contrib/terminal/browser/terminal.service';
import { TerminalChatContextKeys } from './terminalChat.js';
import { TerminalChatController } from './terminalChatController.js';

const _moduleId = "vs/workbench/contrib/terminalContrib/chat/browser/terminalChatAccessibilityHelp";
class TerminalChatAccessibilityHelpContribution extends Disposable {
    static { this.ID = 'terminalChatAccessiblityHelp'; }
    constructor() {
        super();
        this._register(AccessibilityHelpAction.addImplementation(110, 'terminalChat', runAccessibilityHelpAction, TerminalChatContextKeys.focused));
    }
}
async function runAccessibilityHelpAction(accessor) {
    const accessibleViewService = accessor.get(IAccessibleViewService);
    const terminalService = accessor.get(ITerminalService);
    const instance = terminalService.activeInstance;
    if (!instance) {
        return;
    }
    const helpText = getAccessibilityHelpText(accessor);
    accessibleViewService.show({
        id: "terminal-chat" ,
        verbositySettingKey: "accessibility.verbosity.terminalChat" ,
        provideContent: () => helpText,
        onClose: () => TerminalChatController.get(instance)?.focus(),
        options: { type: "help"  }
    });
}
function getAccessibilityHelpText(accessor) {
    const keybindingService = accessor.get(IKeybindingService);
    const content = [];
    const openAccessibleViewKeybinding = keybindingService.lookupKeybinding('editor.action.accessibleView')?.getAriaLabel();
    const runCommandKeybinding = keybindingService.lookupKeybinding("workbench.action.terminal.chat.runCommand" )?.getAriaLabel();
    const insertCommandKeybinding = keybindingService.lookupKeybinding("workbench.action.terminal.chat.insertCommand" )?.getAriaLabel();
    const makeRequestKeybinding = keybindingService.lookupKeybinding("workbench.action.terminal.chat.makeRequest" )?.getAriaLabel();
    const startChatKeybinding = keybindingService.lookupKeybinding("workbench.action.terminal.chat.start" )?.getAriaLabel();
    const focusResponseKeybinding = keybindingService.lookupKeybinding("workbench.action.terminal.chat.focusResponse" )?.getAriaLabel();
    const focusInputKeybinding = keybindingService.lookupKeybinding("workbench.action.terminal.chat.focusInput" )?.getAriaLabel();
    content.push(( localizeWithPath(
        _moduleId,
        0,
        "Inline chat occurs within a terminal. It is useful for suggesting terminal commands. Keep in mind that AI generated code may be incorrect."
    )));
    content.push(( localizeWithPath(
        _moduleId,
        1,
        "It can be activated using the command: Terminal: Start Chat ({0}), which will focus the input box.",
        startChatKeybinding
    )));
    content.push(makeRequestKeybinding ? ( localizeWithPath(
        _moduleId,
        2,
        "The input box is where the user can type a request and can make the request ({0}). The widget will be closed and all content will be discarded when the Escape key is pressed and the terminal will regain focus.",
        makeRequestKeybinding
    )) : ( localizeWithPath(
        _moduleId,
        3,
        "The input box is where the user can type a request and can make the request by tabbing to the Make Request button, which is not currently triggerable via keybindings. The widget will be closed and all content will be discarded when the Escape key is pressed and the terminal will regain focus."
    )));
    content.push(openAccessibleViewKeybinding ? ( localizeWithPath(
        _moduleId,
        4,
        'The response can be inspected in the accessible view ({0}).',
        openAccessibleViewKeybinding
    )) : ( localizeWithPath(
        _moduleId,
        5,
        'With the input box focused, inspect the response in the accessible view via the Open Accessible View command, which is currently not triggerable by a keybinding.'
    )));
    content.push(focusResponseKeybinding ? ( localizeWithPath(
        _moduleId,
        6,
        'Reach the response from the input box ({0}).',
        focusResponseKeybinding
    )) : ( localizeWithPath(
        _moduleId,
        7,
        'Reach the response from the input box by tabbing or assigning a keybinding for the command: Focus Terminal Response.'
    )));
    content.push(focusInputKeybinding ? ( localizeWithPath(
        _moduleId,
        8,
        'Reach the input box from the response ({0}).',
        focusInputKeybinding
    )) : ( localizeWithPath(
        _moduleId,
        9,
        'Reach the response from the input box by shift+tabbing or assigning a keybinding for the command: Focus Terminal Input.'
    )));
    content.push(runCommandKeybinding ? ( localizeWithPath(
        _moduleId,
        10,
        'With focus in the input box or command editor, the Terminal: Run Chat Command ({0}) action.',
        runCommandKeybinding
    )) : ( localizeWithPath(
        _moduleId,
        11,
        'Run a command by tabbing to the button as the action is currently not triggerable by a keybinding.'
    )));
    content.push(insertCommandKeybinding ? ( localizeWithPath(
        _moduleId,
        12,
        'With focus in the input box command editor, the Terminal: Insert Chat Command ({0}) action.',
        insertCommandKeybinding
    )) : ( localizeWithPath(
        _moduleId,
        13,
        'Insert a command by tabbing to the button as the action is currently not triggerable by a keybinding.'
    )));
    content.push(( localizeWithPath(
        _moduleId,
        14,
        "Use tab to reach conditional parts like commands, status, message responses and more."
    )));
    content.push(( localizeWithPath(
        _moduleId,
        15,
        "Accessibility Signals can be changed via settings with a prefix of signals.chat. By default, if a request takes more than 4 seconds, you will hear a sound indicating that progress is still occurring."
    )));
    return content.join('\n\n');
}

export { TerminalChatAccessibilityHelpContribution, getAccessibilityHelpText, runAccessibilityHelpAction };
